import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { useAuth } from '../contexts/AuthContext';
import { keyframes } from '@mui/system';
import StandardCard from './common/StandardCard';
import DashboardHero from './shared/DashboardHero';
import ExpandableCard from './shared/ExpandableCard';
import ViewModeBanner from './shared/ViewModeBanner';
import ClinicSelector from './shared/ClinicSelector';

// Icons
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import MessageIcon from '@mui/icons-material/Message';
import NotificationBell from './notifications/NotificationBell';
import PaymentIcon from '@mui/icons-material/Payment';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CopyrightIcon from '@mui/icons-material/Copyright';
// New icons for sponsor connected services
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import ScienceIcon from '@mui/icons-material/Science';
import RadiologyIcon from '@mui/icons-material/MedicalServices';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StoreIcon from '@mui/icons-material/Store';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const SponsorDashboard = () => {
  const { profile, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Mock data for sponsored patients
  const [sponsoredPatients] = useState([
    {
      id: 1,
      name: 'Maria Katema',
      relationship: 'Mother',
      location: 'Lusaka, Zambia',
      consentStatus: 'approved',
      lastAppointment: '2025-01-05',
      nextAppointment: '2025-01-20',
      healthStatus: 'stable'
    },
    {
      id: 2,
      name: 'John Katema',
      relationship: 'Father',
      location: 'Ndola, Zambia',
      consentStatus: 'pending',
      lastAppointment: '2024-12-15',
      nextAppointment: 'pending',
      healthStatus: 'monitoring'
    }
  ]);

  // Mock payment data
  const [paymentSummary] = useState({
    monthlyBudget: 500,
    currentSpent: 320,
    pendingCharges: 85,
    lastPayment: '2025-01-01'
  });

  // Mock recent activities
  const [recentActivities] = useState([
    {
      id: 1,
      type: 'appointment',
      patient: 'Maria Katema',
      description: 'Video consultation with Dr. Mwanza completed',
      date: '2025-01-05',
      cost: 75
    },
    {
      id: 2,
      type: 'prescription',
      patient: 'Maria Katema',
      description: 'Prescription filled at local pharmacy',
      date: '2025-01-03',
      cost: 45
    },
    {
      id: 3,
      type: 'consent',
      patient: 'John Katema',
      description: 'Consent request sent for health information sharing',
      date: '2025-01-02',
      cost: 0
    }
  ]);

  const navigate = useNavigate();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signout fails, navigate to login
      navigate('/login', { replace: true });
    }
  };


  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <ViewModeBanner />
      {/* Header */}
      <AppBar 
        position="static" 
        color="primary" 
        elevation={2}
        sx={{
          borderBottom: 'none',
          bgcolor: 'primary.dark',
          color: 'common.white',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box 
              component="div" 
              sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                animation: `${slideIn} 0.5s ease-out`,
              }}
            >
              <ClinicSelector />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationBell />
              <Tooltip title="Account">
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 180,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem component={Link} to="/sponsor/profile" onClick={handleClose}>
                  <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem component={Link} to="/sponsor/settings" onClick={handleClose}>
                  <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Hero Section */}
      <DashboardHero
        greeting={`Welcome${profile?.first_name ? `, ${profile.first_name}` : ''}!`}
        description="Support your loved ones' healthcare from abroad."
        showVideoCallButton={true}
        showScheduleButton={true}
        scheduleButtonDisabled={true}
        scheduleButtonTooltip="Appointments are managed at the clinic level for your sponsored patients"
        userType="sponsor"
      />
      
      {/* Main Content */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          mb: 6, 
          flex: 1, 
          px: { xs: 1, sm: 2 }, 
          width: '100%', 
          maxWidth: '100%' 
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
          {/* Sponsorship Management - Moved up for better visibility */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 }, // Reduced padding for mobile
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              animation: `${fadeIn} 0.5s ease-out 0.1s both`,
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Sponsorship Management
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 2.5, md: 3 },
                justifyContent: { xs: 'stretch', sm: 'center' },
                alignItems: 'stretch',
                mb: 2,
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '240px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(25% - 18px)' } }}>
                <StandardCard 
                  icon={WhatsAppIcon} 
                  title="WhatsApp Support" 
                  description="Connect with team via WhatsApp"
                  link="#" 
                  color="success"
                  buttonText="Open WhatsApp"
                  disabled={false}
                  isWhatsApp={true}
                  whatsAppNumber="15551234567"
                  whatsAppMessage="Hello! This is a sponsor from Alpha01 Medical Consultants. I need to discuss partnership opportunities."
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '240px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(25% - 18px)' } }}>
                <StandardCard 
                  icon={PaymentIcon} 
                  title="Payments" 
                  description="View billing and payment history"
                  link="/sponsor/payments" 
                  color="success"
                  buttonText="Manage"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '240px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(25% - 18px)' } }}>
                <StandardCard 
                  icon={MessageIcon} 
                  title="Communications" 
                  description="Messages from care teams"
                  link="/messages?userType=sponsor" 
                  color="info"
                  buttonText="Manage"
                  disabled={false}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)', md: '0 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '240px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)', md: 'calc(25% - 18px)' } }}>
                <StandardCard 
                  icon={HealthAndSafetyIcon} 
                  title="Health Reports" 
                  description="Access health summaries"
                  link="/sponsor/family" 
                  color="warning"
                  buttonText="Manage"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>

          {/* Sponsored Patients - Updated to list format */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{
              fontWeight: 600,
              mb: { xs: 3, md: 4 },
              textAlign: 'center',
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}>
              Your Sponsored Patients
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {sponsoredPatients.map((patient, index) => (
                <Box
                  key={patient.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    animation: `${fadeIn} 0.5s ease-out ${index * 0.1}s both`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }
                  }}
                >
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {patient.name}
                      </Typography>
                      <Chip 
                        icon={patient.consentStatus === 'approved' ? <CheckCircleIcon fontSize="small" /> : <PendingIcon fontSize="small" />}
                        label={patient.consentStatus === 'approved' ? 'Approved' : 'Pending'} 
                        size="small" 
                        color={patient.consentStatus === 'approved' ? 'success' : 'warning'}
                        sx={{ 
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }} 
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {patient.relationship} • {patient.location}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Last: {patient.lastAppointment} | Next: {patient.nextAppointment}
                      </Typography>
                      <Chip 
                        label={patient.healthStatus} 
                        size="small" 
                        color={patient.healthStatus === 'stable' ? 'success' : 'info'}
                        sx={{ 
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }} 
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Payment Summary & Recent Activities */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 4 }}>
            {/* Payment Summary */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out 0.3s both`,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}>
                Payment Summary
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Monthly Budget</Typography>
                  <Typography variant="h6" color="primary.main">${paymentSummary.monthlyBudget}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Current Spent</Typography>
                  <Typography variant="h6" color="success.main">${paymentSummary.currentSpent}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Pending Charges</Typography>
                  <Typography variant="h6" color="warning.main">${paymentSummary.pendingCharges}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Remaining Budget</Typography>
                  <Typography variant="h6" color="info.main">
                    ${paymentSummary.monthlyBudget - paymentSummary.currentSpent - paymentSummary.pendingCharges}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Recent Activities */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: `${fadeIn} 0.5s ease-out 0.4s both`,
              }}
            >
              <Typography variant="h6" gutterBottom sx={{
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}>
                Recent Activities
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {recentActivities.map((activity) => (
                  <Box key={activity.id} sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {activity.patient}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activity.date}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {activity.description}
                    </Typography>
                    {activity.cost > 0 && (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        ${activity.cost}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Financial Management - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Financial Management
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 2.5, md: 3 },
                justifyContent: { xs: 'stretch', sm: 'center' },
                alignItems: 'stretch',
                mb: 2,
                width: '100%',
                maxWidth: { xs: '100%', md: '700px' },
                mx: 'auto',
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={AccountBalanceIcon} 
                  title="Banking" 
                  description="Manage payment methods & banking"
                  link="#" 
                  color="primary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={CreditCardIcon} 
                  title="Auto-Pay" 
                  description="Set up automatic payments"
                  link="#" 
                  color="success"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={ReceiptIcon} 
                  title="Receipts" 
                  description="Download payment receipts"
                  link="#" 
                  color="info"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={TrendingUpIcon} 
                  title="Spending Analytics" 
                  description="View spending trends & reports"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>

          {/* Connected Healthcare Services - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Connected Healthcare Services
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 2.5, md: 3 },
                justifyContent: { xs: 'stretch', sm: 'center' },
                alignItems: 'stretch',
                mb: 2,
                width: '100%',
                maxWidth: { xs: '100%', md: '700px' },
                mx: 'auto',
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={LocalPharmacyIcon} 
                  title="Pharmacy Network" 
                  description="Monitor prescription costs & coverage"
                  link="#" 
                  color="success"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={ScienceIcon} 
                  title="Lab Services" 
                  description="Track lab test expenses & results"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={RadiologyIcon} 
                  title="Imaging Services" 
                  description="Manage imaging costs & reports"
                  link="#" 
                  color="info"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={DirectionsCarIcon} 
                  title="Transport Services" 
                  description="Coordinate & pay for medical transport"
                  link="#" 
                  color="primary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>

          {/* Support & Communication Services - Updated layout to match Health Stats */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              width: '100%',
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2, sm: 3, md: 4 },
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              }}
            >
              Support & Communication Services
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 2.5, md: 3 },
                justifyContent: { xs: 'stretch', sm: 'center' },
                alignItems: 'stretch',
                mb: 2,
                width: '100%',
                maxWidth: { xs: '100%', md: '700px' },
                mx: 'auto',
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={WhatsAppIcon} 
                  title="WhatsApp Updates" 
                  description="Get real-time updates via WhatsApp"
                  link="#" 
                  color="success"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={SendIcon} 
                  title="Care Coordination" 
                  description="Communicate with care teams"
                  link="#" 
                  color="primary"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 10px)' }, minWidth: { xs: '100%', sm: '280px' }, maxWidth: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                <StandardCard 
                  icon={StoreIcon} 
                  title="Local Services" 
                  description="Connect to local stores & services"
                  link="#" 
                  color="warning"
                  buttonText="Coming Soon"
                  disabled={true}
                  showDescriptionOnHover={true}
                />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: { xs: 5, md: 6 }, 
          bgcolor: 'primary.dark',
          color: 'white',
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'primary.main',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 4, md: 0 },
              mb: 3,
            }}
          >
            {/* Logo, description, social */}
            <Box sx={{ flex: 1, minWidth: 220, mb: { xs: 3, md: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo.svg"
                    alt="Alpha01 Medical Consultants Logo - Telemedicine Platform"
                    sx={{
                      height: { xs: 56, md: 40 },
                      mr: 1,
                      display: 'block',
                      borderRadius: 2,
                      padding: 0.5,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.05)' },
                    }}
                  />
                </Box>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  color: 'rgba(255,255,255,0.85)', 
                  fontWeight: 400, 
                  fontSize: { xs: '1.08rem', md: '1.15rem' }, 
                  fontStyle: 'italic',
                  letterSpacing: 0.1,
                  lineHeight: 1.7,
                }}
              >
                Supporting healthcare access across borders.<br />
                Connecting diaspora families with quality care.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <TwitterIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <InstagramIcon />
                </IconButton>
                <IconButton color="inherit" size="small" sx={{ '&:hover': { color: 'secondary.light' } }}>
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>
            {/* Contact Info */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Support
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Sponsor Support Line
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Phone: +1 (555) 123-SPONSOR
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Email: sponsor@alpha01med.com
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
                Available 24/7 for urgent matters
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)' }}>
              <CopyrightIcon fontSize="small" sx={{ mr: 0.5 }} /> {new Date().getFullYear()} Alpha01 Medical Consultants. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, md: 0 } }}>
              <Typography variant="body2" component={Link} to="/privacy" sx={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 400 }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" component={Link} to="/terms" sx={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 400 }}>
                Terms of Service
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SponsorDashboard;
